import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, API } from '../../App';
import { useHubT } from './hubStrings';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const HELP_CATS = ['help_needed', 'help_offered'];

export const ReportComposer = ({ open, onClose, incidentId = null, defaultCategory = 'general_update', defaultTown = '', onPosted }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hubT = useHubT();
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    category: defaultCategory, town: defaultTown, area: '', body: '',
    helpType: 'transport', witnessed: false, timeSeen: '',
  });

  useEffect(() => {
    if (open) setForm(f => ({ ...f, category: defaultCategory, town: f.town || defaultTown }));
  }, [open, defaultCategory, defaultTown]);

  useEffect(() => {
    if (!open || meta) return;
    axios.get(`${API}/incidents/meta`).then(r => setMeta(r.data)).catch(() => {});
  }, [open, meta]);

  if (open && !user) {
    return (
      <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent data-testid="report-composer-signin">
          <DialogHeader><DialogTitle>{hubT('signInToPost')}</DialogTitle></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>{hubT('cancel')}</Button>
            <Button
              onClick={() => navigate('/signin', { state: { from: `${location.pathname}${location.search || ''}` } })}
              className="bg-slate-800 text-white"
              data-testid="report-composer-signin-btn"
            >
              {hubT('signInToPost')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const isHelp = HELP_CATS.includes(form.category);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        incidentId: incidentId || undefined,
        town: form.town,
        category: form.category,
        area: form.area,
        body: form.body,
        witnessed: form.witnessed,
        timeSeen: form.timeSeen ? new Date(form.timeSeen).toISOString() : undefined,
        helpType: isHelp ? form.helpType : undefined,
      };
      const r = await axios.post(`${API}/community-reports`, payload, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      toast.success(hubT('reportPosted'));
      onPosted?.(r.data.report);
      onClose();
      setForm(f => ({ ...f, area: '', body: '', witnessed: false, timeSeen: '' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not post report');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="report-composer-dialog">
        <DialogHeader><DialogTitle>{hubT('newReport')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-gray-700">{hubT('category')}</label>
              <select
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full border rounded-md px-2 py-2 text-sm bg-white mt-1"
                data-testid="report-category-select"
              >
                {(meta?.reportCategories || ['general_update']).map(c => (
                  <option key={c} value={c}>{hubT(`rc_${c}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">{hubT('town')}</label>
              <select
                value={form.town}
                onChange={e => setForm(f => ({ ...f, town: e.target.value }))}
                className="w-full border rounded-md px-2 py-2 text-sm bg-white mt-1"
                data-testid="report-town-select"
              >
                <option value="">—</option>
                {(meta?.towns || []).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          {isHelp && (
            <div>
              <label className="text-xs font-semibold text-gray-700">{hubT('helpType')}</label>
              <select
                value={form.helpType}
                onChange={e => setForm(f => ({ ...f, helpType: e.target.value }))}
                className="w-full border rounded-md px-2 py-2 text-sm bg-white mt-1"
                data-testid="report-helptype-select"
              >
                {(meta?.helpTypes || ['transport']).map(h => (
                  <option key={h} value={h}>{hubT(`ht_${h}`)}</option>
                ))}
              </select>
            </div>
          )}
          <Input
            value={form.area}
            onChange={e => setForm(f => ({ ...f, area: e.target.value }))}
            placeholder={hubT('area')}
            maxLength={80}
            data-testid="report-area-input"
          />
          <Textarea
            value={form.body}
            onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
            placeholder={hubT('describe')}
            rows={6}
            maxLength={10000}
            data-testid="report-body-input"
          />
          <div className="grid grid-cols-2 gap-2 items-end">
            <div>
              <label className="text-xs font-semibold text-gray-700">{hubT('timeSeen')}</label>
              <Input
                type="datetime-local"
                value={form.timeSeen}
                onChange={e => setForm(f => ({ ...f, timeSeen: e.target.value }))}
                data-testid="report-timeseen-input"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.witnessed}
                onChange={e => setForm(f => ({ ...f, witnessed: e.target.checked }))}
                className="h-4 w-4"
                data-testid="report-witnessed-checkbox"
              />
              {hubT('witnessed')}
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{hubT('cancel')}</Button>
          <Button
            onClick={submit}
            disabled={busy || !form.town || form.body.trim().length < 10}
            className="bg-slate-800 hover:bg-slate-900 text-white"
            data-testid="report-composer-submit"
          >
            {busy ? '…' : hubT('submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportComposer;
